import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DEFAULT_APP_LOCALE, isSupportedAppLocale, normalizeAppLocale } from './locales';

export const APP_ROUTE_SEGMENTS = {
  home: '',
  courses: 'courses',
  video: 'video',
  tutor: 'tutor',
  profile: 'profile',
  subjects: 'subjects',
  grades: 'grades',
  reels: 'reels',
  upload: 'upload',
};

export function stripLocaleFromPath(pathname) {
  const segments = String(pathname || '/').split('/').filter(Boolean);
  if (segments.length === 0) {
    return '/';
  }

  if (isSupportedAppLocale(segments[0])) {
    const remainder = segments.slice(1).join('/');
    return remainder ? `/${remainder}` : '/';
  }

  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function buildLocalizedPath(locale, pathOrKey = '') {
  const normalizedLocale = normalizeAppLocale(locale);
  const rawPath = APP_ROUTE_SEGMENTS[pathOrKey] ?? pathOrKey;
  const cleanedPath = String(rawPath || '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');

  return cleanedPath ? `/${normalizedLocale}/${cleanedPath}` : `/${normalizedLocale}`;
}

export function localizePathname(pathname, locale) {
  return buildLocalizedPath(locale, stripLocaleFromPath(pathname));
}

export function resolveInitialLocale(language) {
  return normalizeAppLocale(String(language || '').split('-')[0] || DEFAULT_APP_LOCALE);
}

export function useCurrentLocale() {
  const { locale } = useParams();
  return normalizeAppLocale(locale);
}

export function useLocalizedNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useCurrentLocale();

  return useMemo(
    () => ({
      locale,
      buildPath: (pathOrKey = '') => buildLocalizedPath(locale, pathOrKey),
      navigateTo: (pathOrKey = '', options) => navigate(buildLocalizedPath(locale, pathOrKey), options),
      switchLocale: (nextLocale, options) => navigate(localizePathname(location.pathname, nextLocale), options),
    }),
    [locale, location.pathname, navigate]
  );
}
